/**
 * Line ordering + delivery estimate for the cart surfaces (cart page and cart
 * drawer).
 *
 * Two separate jobs, deliberately on different footings:
 *
 *   1. GROUPING (renderGroups) - the lines are bucketed under a "Shipping ..."
 *      heading each, ready-to-ship first, then soonest departure. Driven by
 *      ship-from (ESDeliveryETA.baseDate), which needs no customer location, so
 *      every shopper sees it. Runs synchronously, no network.
 *   2. DELIVERY ESTIMATE - one "Estimated delivery ..." line for the whole order
 *      above the totals, based on the SLOWEST line's ARRIVAL, using the shared
 *      calculation the PDP store locator uses (assets/delivery-eta.js) so a
 *      product can never quote one date on its page and another in the cart.
 *      Needs the customer's location, so it stays hidden without one.
 *
 * The split matters: headings are about when stock leaves the warehouse, the
 * estimate is about when it lands. Grouping on arrival would have meant no
 * grouping at all for the majority of shoppers, who have never set a location.
 *
 * Two honest limitations on the ESTIMATE, both by design:
 *   1. It needs the customer's location, and the cart has no location field
 *      (removed 23 Jul) - so it reads whatever they set on a product page and
 *      stays hidden when there is nothing. No location, no promise.
 *   2. Home delivery and store collection run to different timescales, and the
 *      customer does not choose a method until checkout. We follow what they
 *      set: a postcode means home delivery, a store on its own means
 *      collection. If they set both, the postcode wins (home delivery is the
 *      default path and the summary already says the final timeframe is
 *      confirmed at checkout).
 *
 * Usage: ESCartShipment.render(root, label) where `root` carries
 * data-transit-url + data-country and contains the [data-cart-line] rows plus,
 * optionally, [data-cart-shipment] (+ [data-cart-shipment-label]) and
 * [data-cart-estimate] (+ [data-cart-estimate-heading]). Scoping every query to
 * `root` matters: the cart drawer is globally present and carries the same
 * [data-cart-line] hook, so an unscoped query would count every line twice on
 * the cart page and could read the other surface's copies.
 */
window.ESCartShipment = window.ESCartShipment || (function () {
  // Guarded: the cart page and the globally-present cart drawer each load this
  // file, so on the cart page it executes twice. Keeping the first instance
  // keeps one shared etaCache (one transit fetch for both surfaces).
  var etaCache = {};

  function fetchEta(path) {
    if (etaCache[path]) return etaCache[path];
    var p = fetch(path)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    etaCache[path] = p;
    return p;
  }

  function lineOpts(line) {
    return {
      stockAvailable: parseInt(line.getAttribute('data-stock-available'), 10) || 0,
      etaTs: parseInt(line.getAttribute('data-eta'), 10) || 0,
      isPreorder: line.hasAttribute('data-preorder')
    };
  }

  /**
   * When a line can leave the warehouse - the one place that answers it.
   *
   * A native Shopify bundle has no Rex SKU of its own, so the parent line
   * carries no stock metafield and no ETA and used to fall out of everything:
   * no group, no per-line date, and skipped by the delivery estimate as well.
   * Its COMPONENTS are ordinary ESR products that do have the data, so the
   * bundle ships when its slowest component does (client direction, #19498858).
   *
   * The components ride on the row as JSON (data-components) and are reduced
   * here rather than in Liquid, so baseDate's precedence rules - pre-order
   * beats stock, stock beats ETA - are applied by the module that owns them
   * instead of being restated in a second language.
   */
  function effectiveOpts(line, ESD) {
    var own = lineOpts(line);
    var raw = line.getAttribute('data-components');
    if (!raw) return own;

    var parts;
    try { parts = JSON.parse(raw); } catch (e) { parts = null; }
    if (!parts || !parts.length) return own;

    /* The slowest component's OWN inputs are returned, not just its date, so
       every consumer benefits: the grouping, the per-line date, and the delivery
       estimate below the totals, which needs the raw opts to run arrival maths
       on. A bundle used to be skipped by all three. */
    var slowest = null;
    parts.forEach(function (p) {
      var opts = {
        stockAvailable: p.stock || 0,
        etaTs: p.eta || 0,
        isPreorder: !!p.preorder
      };
      var base = ESD.baseDate(opts);
      if (!base.date) return;
      // One component with no honest date of its own does not sink the bundle -
      // the remaining ones still give a date, which beats showing none at all.
      if (!slowest || base.date.getTime() > slowest.at) {
        slowest = { at: base.date.getTime(), opts: opts };
      }
    });
    return slowest ? slowest.opts : own;
  }

  /**
   * When a line can leave the warehouse. A bundle is only ready to ship when
   * every component is, which falls out of using the slowest component's opts.
   */
  function lineBase(line, ESD) {
    var base = ESD.baseDate(effectiveOpts(line, ESD));
    if (line.hasAttribute('data-components') && base.date) {
      base = { date: base.date, confident: base.confident, why: 'bundle, slowest component -> ' + base.why };
    }
    return base;
  }

  function slice(nodes) {
    return Array.prototype.slice.call(nodes);
  }

  /* ---------------------------------------------------------------------- *
   * Shipment grouping                                                      *
   * ---------------------------------------------------------------------- */

  /**
   * Split the cart's lines into TWO buckets: in stock, then everything else.
   *
   * Grouped on SHIP-FROM (ESDeliveryETA.baseDate) - when the item can leave the
   * warehouse - NOT on arrival. baseDate needs no customer location, so every
   * shopper gets the grouping; arrival needs a postcode or store, which most
   * shoppers have never set (it can only be captured on a product page), so
   * grouping on it would mean no grouping at all for the majority. The arrival
   * date belongs to the summary's "Estimated delivery" line, which is unchanged
   * and still location-aware.
   *
   * Two buckets, not one per date (client direction, 18 Aug, #19498858). The
   * groups used to be keyed on the heading TEXT, which meant a bucket per
   * distinct day count under four weeks and another per early/late month beyond
   * it - six items with six ETAs produced six headings. The dates are not lost:
   * they moved onto each line (see paintLineDates), which is where the client
   * wanted the per-SKU detail, and lets the count read "1 of 3 items" against a
   * fixed pair of buckets.
   *
   * Lines with no honest date at all are left where they are rather than
   * invented a bucket for - filing them under "not in stock" would assert
   * something we do not know. In practice that is now rare: a native bundle,
   * previously the main case, is answered by its components (see lineBase).
   */
  function buildGroups(lines, ESD) {
    /* `confident` is baseDate's "in stock, ships today" signal - the same flag
       driving the PDP's green dot. The buckets still decide the ORDER of the
       single list; since #1148 they are no longer drawn as groups, so `text` is
       only a label for the console trace below. */
    var ready = { text: 'ready to ship', ready: true, lines: [] };
    var waiting = { text: 'awaiting stock', ready: false, lines: [] };

    lines.forEach(function (line, index) {
      var base = lineBase(line, ESD);
      if (!base.date) return;
      var bucket = base.confident ? ready : waiting;
      bucket.lines.push({ el: line, index: index, at: base.date.getTime(), base: base });
    });

    // In stock: cart order, as added - they all ship today, so there is nothing
    // to sort by. Waiting: soonest first, so the dates now shown on each line
    // read in order down the list.
    ready.lines.sort(function (a, b) { return a.index - b.index; });
    waiting.lines.sort(function (a, b) {
      if (a.at !== b.at) return a.at - b.at;
      return a.index - b.index;
    });

    return [ready, waiting].filter(function (g) { return g.lines.length > 0; });
  }

  /** Units on a row - a line at quantity 3 counts as 3 items. */
  function lineUnits(el) {
    return parseInt(el.getAttribute('data-line-qty'), 10) || 1;
  }

  /**
   * The per-line dispatch date, on every row outside the in-stock bucket.
   *
   * The placeholder is server-rendered in its designed position (between the
   * line's options and its promo badge) and filled here, because this is where
   * the date is already known - and because deriving it in Liquid would fork
   * baseDate's precedence rules away from the module that owns them, letting a
   * product quote one date in the cart and another on its own page.
   *
   * Every pass clears every row first: a quantity change can move a line from
   * one bucket to the other, and a stale date left on an in-stock row would be
   * a promise about the wrong item.
   */
  /* The traffic-light dot beside a line's status (Daniel, 1 Sep, #1148). Built
     as nodes rather than innerHTML: the text half is a date we compose, and
     appending a text node keeps it text no matter what it ever contains.

     Green for in stock, amber for awaiting stock. Both the size and the two
     colours are the PDP store locator's existing .sl-dot - see
     blocks/product-store-locator.liquid, where the same pair marks the delivery
     and pickup rows - so the cart is not inventing a second traffic light. */
  function setLineLabel(el, text, tone) {
    el.textContent = '';
    var dot = document.createElement('span');
    dot.className = 'cart-line__eta-dot cart-line__eta-dot--' + tone;
    dot.setAttribute('aria-hidden', 'true');
    el.appendChild(dot);
    el.appendChild(document.createTextNode(text));
    el.hidden = false;
  }

  function paintLineDates(root, groups, ESD) {
    slice(root.querySelectorAll('[data-cart-line-eta]')).forEach(function (el) {
      el.textContent = '';
      el.hidden = true;
    });
    groups.forEach(function (group) {
      group.lines.forEach(function (entry) {
        var el = entry.el.querySelector('[data-cart-line-eta]');
        if (!el) return;
        /* In-stock rows used to show nothing at all. The client asked for the
           status to be stated on every line, not only the ones that are late
           (Daniel, 1 Sep, #1148) - so the bucket that needs no date says what it
           is instead. "In Stock" is the client's capitalisation, quoted verbatim.

           Plain text on purpose: #1151 owns the styled badges, and Bec is still
           reviewing that design. This is the interim, and it deliberately does
           not anticipate the pill treatment. */
        if (group.ready) {
          setLineLabel(el, 'In Stock', 'success');
          return;
        }
        if (!entry.base || !entry.base.date) return;
        setLineLabel(el, ESD.lineDueMessage(entry.base.date).text, 'warning');
      });
    });
  }

  /**
   * A rule between every pair of adjacent lines, and after the last one when the
   * surface renders one there.
   *
   * Runs over the rebuilt list rather than per group, because a line the
   * grouping skipped still needs its separators: buildGroups drops any line with
   * no honest date, which is every native Shopify bundle (no Rex SKU, so no
   * stock metafield and no ETA). Those lines stay where they are, and with the
   * dividers previously created per group they ended up with none at all - the
   * whole list lost its line breaks as soon as a bundle was in the cart.
   *
   * A group heading between two lines already separates them, so no rule goes
   * there.
   */
  /**
   * Rebuild the list as heading + lines, per group.
   *
   * Runs synchronously off data already on each row, so it does not wait on the
   * transit fetch. The lines are re-ordered in place: the server renders them in
   * cart order and this moves them, so it must re-run after every re-render
   * (both surfaces already call render() then).
   */
  function renderGroups(root, prefix) {
    var ESD = window.ESDeliveryETA;
    var lines = slice(root.querySelectorAll('[data-cart-line]'));
    /* Returns the buckets it built, so the caller can tell a MIXED cart (both
       buckets present) from an all-ready or all-waiting one without repeating
       the work. That distinction is what gates the no-location estimate below. */
    if (!ESD || !lines.length) return null;

    var parent = lines[0].parentNode;

    /* Nothing to clear between passes any more: since #1148 this rebuild adds
       no headings and no rules, it only re-orders the rows that are already
       there. */

    var groups = buildGroups(lines, ESD);
    if (!groups.length) return null;

    // Units in the cart, for the console trace only.
    var cartUnits = lines.reduce(function (sum, line) { return sum + lineUnits(line); }, 0);

    /* Mark the insertion point BEFORE building the fragment. Appending a line to
       the fragment MOVES it out of `parent`, so once the fragment is built
       lines[0] is no longer a child here and insertBefore(_, lines[0]) would
       throw - leaving every line orphaned in a detached fragment, i.e. an empty
       cart. The placeholder is an anchor that cannot be moved out from under us. */
    var placeholder = document.createComment('cart-groups');
    parent.insertBefore(placeholder, lines[0]);

    /* The rows are live DOM being moved, so a throw part-way through would leave
       them in a detached fragment - a cart that looks empty. Any failure puts
       every row back and gives up on grouping instead. */
    try {
      var frag = document.createDocumentFragment();
      groups.forEach(function (group) {
        group.lines.forEach(function (entry) {
          // appendChild MOVES the row out of its current position.
          frag.appendChild(entry.el);
        });
      });

      // Put the rebuilt list back exactly where the lines started, so anything
      // above them (the multibuy progress bar) keeps its place.
      parent.insertBefore(frag, placeholder);
    } catch (err) {
      lines.forEach(function (line) {
        if (!parent.contains(line)) parent.insertBefore(line, placeholder);
      });
      parent.removeChild(placeholder);
      console.error(prefix + ' Shipment grouping failed - cart left ungrouped', err);
      return;
    }
    parent.removeChild(placeholder);

    // Dates onto the waiting rows, after the rebuild: the rows have moved, and
    // painting before the move would be undone by nothing but is harder to read.
    paintLineDates(root, groups, ESD);

    console.log(
      prefix + ' Shipment groups (' + cartUnits + ' items)\n' +
      groups.map(function (g) {
        var count = g.lines.reduce(function (sum, e) { return sum + lineUnits(e.el); }, 0);
        return '  ' + (g.ready ? '[ready] ' : '        ') + g.text + '  (' + count + ' of ' + cartUnits + ')\n' +
          g.lines.map(function (e) {
            var when = g.ready ? '' : '  -> ' + ESD.lineDueMessage(e.base.date).text;
            return '     - ' + e.el.getAttribute('data-line-title') + when +
              (e.base && e.base.why ? '  [' + e.base.why + ']' : '');
          }).join('\n');
      }).join('\n')
    );
    return groups;
  }

  // Persisted location, shared with the PDP store locator + store locator page
  // and the cart page's postcode field (localStorage 'es:store-locator').
  var LOCATION_KEY = 'es:store-locator';
  function loadLocation() {
    try { return JSON.parse(window.localStorage.getItem(LOCATION_KEY)) || {}; }
    catch (e) { return {}; }
  }

  // Deferred callers queue up until delivery-eta.js has run (it is deferred, and
  // a section's inline script can run first). Guaranteed by window load.
  var pending = [];
  function waitForETA(fn) {
    pending.push(fn);
    if (pending.length > 1) return;
    window.addEventListener('load', function () {
      var queued = pending;
      pending = [];
      queued.forEach(function (f) { f(); });
    }, { once: true });
  }

  /* One writer for the TWO copies of the delivery estimate: the summary box and
     the block at the top of the cart. The client asked for the same message in
     both places and said the repetition is intentional (Daniel, 1 Sep, #1148),
     so both are painted from ONE string here rather than derived twice - two
     derivations would be free to drift, and they are meant to agree character
     for character. Passing an empty string is how every bail-out hides them,
     which keeps "no date" from leaving a stale promise on either surface. */
  function paintEstimate(root, text) {
    var box = root.querySelector('[data-cart-estimate]');
    var boxHeading = root.querySelector('[data-cart-estimate-heading]');
    var topHeading = root.querySelector('[data-cart-status-heading]');
    if (boxHeading) boxHeading.textContent = text || '';
    if (box) box.hidden = !text;
    if (topHeading) {
      topHeading.textContent = text || '';
      topHeading.hidden = !text;
    }
  }

  function render(root, logLabel) {
    if (!root) return;
    var prefix = logLabel || '[cart]';
    var estimate = root.querySelector('[data-cart-estimate]');
    /* Since #1148 there is no shipment header element at all - the group
       headings, their counts and the rules between them were removed. What is
       left still has to run: renderGroups() orders the single list by lead time,
       and that must happen whether or not this cart has an estimate box to
       fill, so the presence of LINES is the real gate now. */
    if (!estimate && !root.querySelector('[data-cart-line]')) return;

    // Re-runs after a quantity change can land on a cart we cannot calculate for
    // (the slowest line removed, the location cleared), so every bail-out hides
    // rather than leaving a stale promise on screen.
    function hideShipment() {
      paintEstimate(root, '');
    }

    var ESD = window.ESDeliveryETA;
    if (!ESD) {
      waitForETA(function () { render(root, logLabel); });
      return;
    }

    var lines = Array.prototype.slice.call(root.querySelectorAll('[data-cart-line]'));
    if (!lines.length) { hideShipment(); return; }

    /* Group the lines FIRST and unconditionally. It needs no location and no
       network, so the headings appear for every shopper - unlike the delivery
       estimate below, which stays gated on a saved location. */
    var groups = renderGroups(root, prefix);

    var transitUrl = (root.getAttribute('data-transit-url') || '').replace(/\/$/, '');
    var country = (root.getAttribute('data-country') || 'AU').toUpperCase();
    if (!transitUrl) { hideShipment(); return; }

    var loc = loadLocation();
    // The store locator saves the chosen store's outlet id alongside the index
    // (which is only meaningful inside its own list). A state saved before that
    // change has no outlet, so collection mode simply waits until the customer
    // picks a store again.
    var outlet = loc.outlet || null;

    // Home delivery when we have a postcode; collection when the customer has
    // only picked a store. Nothing set -> nothing shown.
    var mode = loc.postcode ? 'delivery' : (outlet ? 'collection' : null);
    if (!mode) {
      /* No location, so no ARRIVAL date - but DISPATCH needs none. baseDate is
         computed per line without a postcode or a store, which is what already
         drives the group headings and the per-line dates for every shopper. So
         the box stays, headed by the slowest line's dispatch date, and the
         advice underneath ("consider placing a separate order...") reaches the
         shopper it was written for.

         The EXACT date, not the tier wording (Alex, 27 Aug). "Shipping from
         late October" would restate the "Shipping from 17 October 2026" already
         printed on that very line, less precisely and without a year. The box is
         the summary-level home of the same fact, so it says the same thing.

         Gated on a MIXED cart, the same condition the advice line uses. Two
         groups means mixed - buildGroups drops an empty bucket - so an
         all-in-stock cart never reaches here and the heading can never read
         "In stock and ready to ship". */
      var mixed = !!groups && groups.length === 2;
      var slowest = null;
      if (mixed) {
        groups.forEach(function (g) {
          g.lines.forEach(function (e) {
            if (e.base && e.base.date && (!slowest || e.base.date.getTime() > slowest.getTime())) {
              slowest = e.base.date;
            }
          });
        });
      }
      /* Gated on the DATE alone. It used to require the summary box to be
         present; now that the same string also feeds the block at the top of the
         cart, the box's absence must not silence the top one. paintEstimate
         skips whichever element a surface does not have. */
      if (slowest) {
        /* Daniel's terminology applies here too, location or no location (Alex,
           1 Sep): this heading used to read "Shipping from 17 October 2026" and
           now reads "Estimated Delivery October 2026" (lead word per the
           client's text, #1148 items 4 and 5, 3 Sep).
           The only date available before a location is chosen is the DISPATCH
           date, so the month is derived from that - it can therefore read
           earlier than a true delivery month, by roughly the transit time.
           Month granularity absorbs some of that gap but not all of it, which is
           called out to the client rather than papered over. */
        var dispatch = ESD.deliveryMonthMessage(slowest, slowest).text;
        paintEstimate(root, dispatch);
        console.log(prefix + ' Shipment header: no saved location, but dispatch needs none - mixed cart, slowest line ' +
          ESD.fmtCalcDay(slowest) + ' -> "' + dispatch + '".');
        return;
      }
      console.log(prefix + ' Shipment header: no saved location (customers set one on a product page), nothing to calculate.' +
        (!mixed ? ' Cart is not mixed, so the dispatch heading stays hidden too.' : ''));
      hideShipment();
      return;
    }

    // The saved suburb narrows the transit lookup inside the postcode (a
    // postcode can cover suburbs with different transit times). A state saved
    // before the locator captured it has none, in which case the worker answers
    // with the postcode's worst case - never an earlier date than the truth.
    var path = mode === 'delivery'
      ? transitUrl + '/transit?postcode=' + encodeURIComponent(loc.postcode) +
        (loc.suburb ? '&suburb=' + encodeURIComponent(loc.suburb) : '') +
        '&country=' + encodeURIComponent(country)
      : transitUrl + '/collection?outlet=' + encodeURIComponent(outlet) + '&country=' + encodeURIComponent(country);

    fetchEta(path).then(function (data) {
      if (!data || !data.found) { hideShipment(); return; }

      // Slowest line wins. Lines with no date at all (no stock, no ETA) cannot
      // be in a cart in practice, so they are skipped rather than guessed at.
      var worst = null;
      var rows = [];
      lines.forEach(function (line) {
        // Bundles resolve to their slowest component here too, so a bundle now
        // counts toward the slowest line instead of being skipped entirely.
        var opts = effectiveOpts(line, ESD);
        var end, win;
        if (mode === 'delivery') {
          var calc = ESD.homeArrival(opts, data);
          if (!calc) return;
          // Late end for "slowest line"; the window itself is carried for the
          // estimate, which shows dates now transit has a min and a max (#1072).
          end = calc.arrival;
          win = calc;
        } else {
          win = ESD.collectionWindow(opts, data);
          if (!win) return;
          end = win.max;
        }
        rows.push({
          el: line,
          title: line.getAttribute('data-line-title'),
          end: end,
          start: mode === 'delivery' ? win.arrivalMin : win.min,
          why: win.why,
          confident: win.confident
        });
        if (!worst || end > worst.end) {
          worst = {
            end: end,
            start: mode === 'delivery' ? win.arrivalMin : win.min,
            confident: win.confident,
            ready: win.confident
          };
        }
      });
      if (!worst) { hideShipment(); return; }

      var ship = ESD.shippingMessage(worst.end, null, worst.ready && ESD.tierFor(worst.end) === 'near');
      /* ONE sentence for every mode and every tier (Alex, 1 Sep). Collection
         used to get its own "Ready for collection ..." wording on the grounds
         that an order being collected is not a delivery. The client does not
         make that distinction and asked for their terminology throughout, so
         the carve-out is gone: a collection window renders "Delivery in
         <month>" from the same min/max pair as a delivery window. Every state
         that HAS a date now says the same thing; the only headingless state
         left is the one with no date at all. */
      /* Since 3 Sep (#1148 items 4 and 5) the heading carries the product
         page's OWN range text for the slowest line - "Estimated Delivery 12 - 15
         September", "Estimated Delivery late October" - so the cart and the
         product page cannot quote different dates for the same item and
         location. deliveryMonthMessage (month + year) now serves only the
         no-location fallback above. */
      var deliver = ESD.estimateHeadingMessage(worst.start, worst.end, mode);

      /* Per-line: the same range, per row, replacing the dispatch-date label
         renderGroups painted before the lookup answered. Only rows outside the
         in-stock bucket (confident is the flag buildGroups sorts on), so an
         in-stock row keeps "In Stock" and its green dot exactly as before. Each
         row quotes ITS window, not the cart's slowest, which is what lets the
         shopper see which item is holding the order up. */
      rows.forEach(function (r) {
        if (r.confident || !r.start) return;
        var el = r.el.querySelector('[data-cart-line-eta]');
        if (!el) return;
        setLineLabel(el, ESD.lineArrivalMessage(r.start, r.end, mode).text, 'warning');
      });

      // Show the working for QA, same style as the store locator logs.
      var lineLog = rows.map(function (r) {
        var when = r.start && r.start.getTime() !== r.end.getTime()
          ? ESD.fmtCalcDay(r.start) + ' to ' + ESD.fmtCalcDay(r.end)
          : ESD.fmtCalcDay(r.end);
        return '     - ' + when + '  ' + r.title + '  (' + r.why + ')';
      }).join('\n');
      console.log(
        prefix + ' Shipment / delivery estimate\n' +
        '  1. mode:           ' + mode + (mode === 'delivery' ? ' (postcode ' + loc.postcode + ')' : ' (outlet ' + outlet + ')') + '\n' +
        '  2. lines:\n' + lineLog + '\n' +
        '  3. slowest line:   ' + (worst.start && worst.start.getTime() !== worst.end.getTime()
            ? ESD.fmtCalcDay(worst.start) + ' to ' + ESD.fmtCalcDay(worst.end)
            : ESD.fmtCalcDay(worst.end)) +
          '  (' + deliver.days + ' days away at the late end, tier ' + deliver.tier + ')\n' +
        '  4. header:         -> "' + ship.text + '"\n' +
        '  5. estimate:       -> "' + deliver.text + '"'
      );

      /* The whole-cart header is no longer painted: renderGroups() owns the
         headings now and uses this element purely as its template. `ship` is
         still computed above because the log above reports it. */
      paintEstimate(root, deliver.text);
    });
  }

  return {
    render: render,
    loadLocation: loadLocation
  };
})();
